import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { navigate } from "gatsby";
import {
  fetchUserActivePlanDetails,
  sendFormOtp as sendFormOtpRequest,
  verifyFormOtp as verifyFormOtpRequest,
  sendSignupOtp as sendSignupOtpRequest,
  verifySignupOtp as verifySignupOtpRequest,
} from "../services/auth";

const AuthContext = createContext(null);

// Storage keys - centralized for consistency
export const AUTH_STORAGE_KEYS = {
  TOKEN: "auth_token",
  USER_ID: "user_id",
  USER_DATA: "user_data",
};

/**
 * Read persisted auth from storage (browser only).
 * Returns { token, userId, user } or null if not authenticated.
 */
function readStoredAuth() {
  if (typeof window === "undefined") return null;
  try {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
    const userId = localStorage.getItem(AUTH_STORAGE_KEYS.USER_ID);
    const userDataRaw = localStorage.getItem(AUTH_STORAGE_KEYS.USER_DATA);
    let user = null;
    if (userDataRaw) {
      try {
        user = JSON.parse(userDataRaw);
      } catch {
        user = userId ? { id: userId } : null;
      }
    } else if (userId) {
      user = { id: userId };
    }
    if (token || userId) {
      return { token: token || null, userId: userId || (user?.id && String(user.id)) || null, user };
    }
    return null;
  } catch (e) {
    console.error("[Auth] Error reading stored auth:", e);
    return null;
  }
}

/**
 * Persist auth to storage (browser only).
 */
function persistAuth(token, userId, user) {
  if (typeof window === "undefined") return;
  try {
    if (token) localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token);
    else localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN);

    const uid = userId ?? user?.id ?? user?.uid ?? user?.user_id;
    if (uid != null) localStorage.setItem(AUTH_STORAGE_KEYS.USER_ID, String(uid));
    else localStorage.removeItem(AUTH_STORAGE_KEYS.USER_ID);

    if (user && typeof user === "object") {
      localStorage.setItem(AUTH_STORAGE_KEYS.USER_DATA, JSON.stringify(user));
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEYS.USER_DATA);
    }
  } catch (e) {
    console.error("[Auth] Error persisting auth:", e);
  }
}

/**
 * Clear all auth from storage (exported for use by http interceptor on 401).
 */
export function clearStoredAuth() {
  if (typeof window === "undefined") return;
  try {
    Object.values(AUTH_STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
  } catch (e) {
    console.error("[Auth] Error clearing stored auth:", e);
  }
}

/**
 * City is a mandatory profile field. Normalize how it appears on user objects from the API.
 * @param {object|null|undefined} user
 * @returns {string} trimmed city or empty string if missing
 */
export function getUserCity(user) {
  if (!user || typeof user !== "object") return "";
  const raw = user.city ?? user.City ?? user.user_city;
  if (raw == null) return "";
  return String(raw).trim();
}

/** @param {object|null|undefined} user */
export function userHasRequiredCity(user) {
  return getUserCity(user).length > 0;
}

/**
 * Clear all cache (sessionStorage and app-specific localStorage).
 * Call on logout to remove dashboard view, bidder name, search caches, etc.
 */
function clearAllCache() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.clear();
    localStorage.removeItem("userId");
  } catch (e) {
    console.error("[Auth] Error clearing cache:", e);
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const userId = user?.id ?? user?.uid ?? user?.user_id ?? null;

  // Hydrate from storage on mount (handles page refresh, new tab)
  useEffect(() => {
    const stored = readStoredAuth();
    if (stored) {
      setToken(stored.token);
      setUser(stored.user || (stored.userId ? { id: stored.userId } : null));
    } else {
      setToken(null);
      setUser(null);
    }
    setIsLoading(false);
  }, []);

  // Listen for 401 session expiry from HTTP interceptor
  useEffect(() => {
    const handler = () => {
      clearStoredAuth();
      clearAllCache();
      setToken(null);
      setUser(null);
    };
    window.addEventListener("auth:session-expired", handler);
    return () => window.removeEventListener("auth:session-expired", handler);
  }, []);

  // Sync auth from storage when it changes (e.g. another tab logged in/out)
  useEffect(() => {
    const handler = () => {
      const stored = readStoredAuth();
      if (stored) {
        setToken(stored.token);
        setUser(stored.user || (stored.userId ? { id: stored.userId } : null));
      } else {
        setToken(null);
        setUser(null);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const login = useCallback((userData, authToken) => {
    const u = userData && typeof userData === "object" ? userData : null;
    const t = authToken != null ? String(authToken) : null;
    const uid = u?.id ?? u?.user_id ?? (u && typeof u === "object" ? null : null);

    let normalizedUser = u;
    if (u && !Array.isArray(u.loginPlans)) {
      const rawPlans = u.plans;
      normalizedUser = {
        ...u,
        loginPlans: Array.isArray(rawPlans) ? rawPlans : [],
      };
    }

    persistAuth(t, uid, normalizedUser);
    setToken(t);
    setUser(normalizedUser || (uid ? { id: String(uid) } : null));
  }, []);

  const logout = useCallback(() => {
    clearStoredAuth();
    clearAllCache();
    setToken(null);
    setUser(null);
    if (typeof window !== "undefined") {
      navigate("/");
    }
  }, []);

  const setUserPlans = useCallback((planResponse) => {
    const plans = Array.isArray(planResponse) ? planResponse : (planResponse?.plans ?? []);
    setUser((prev) => {
      if (!prev || typeof prev !== "object") return prev;
      const nextUser = { ...prev, plans, loginPlans: plans };
      persistAuth(token, nextUser?.id ?? nextUser?.uid ?? nextUser?.user_id, nextUser);
      return nextUser;
    });
  }, [token]);

  const refreshUserPlans = useCallback(async (uid) => {
    const resolvedUserId = uid ?? userId;
    if (resolvedUserId == null || resolvedUserId === "") return null;
    const planResponse = await fetchUserActivePlanDetails(resolvedUserId);
    setUserPlans(planResponse);
    return planResponse;
  }, [userId, setUserPlans]);

  const sendFormOtp = useCallback((phone) => sendFormOtpRequest(phone), []);

  /** Same endpoint as sendFormOtp; for public forms “Resend OTP”. */
  const resendFormOtp = useCallback((phone) => sendFormOtpRequest(phone), []);

  const verifyFormOtp = useCallback((sessionId, otp) => verifyFormOtpRequest(sessionId, otp), []);

  const sendSignupOtp = useCallback((phone) => sendSignupOtpRequest(phone), []);

  /** Same endpoint as sendSignupOtp; strict signup “Resend OTP”. */
  const resendSignupOtp = useCallback((phone) => sendSignupOtpRequest(phone), []);

  const verifySignupOtp = useCallback((sessionId, otp) => verifySignupOtpRequest(sessionId, otp), []);

  const city = getUserCity(user) || null;
  const hasCity = userHasRequiredCity(user);

  const value = {
    user,
    userId: userId ? String(userId) : null,
    token,
    isAuthenticated: !!(token || userId),
    city,
    hasCity,
    isLoading,
    login,
    logout,
    setUserPlans,
    refreshUserPlans,
    sendFormOtp,
    resendFormOtp,
    verifyFormOtp,
    sendSignupOtp,
    resendSignupOtp,
    verifySignupOtp,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    return {
      user: null,
      userId: null,
      token: null,
      isAuthenticated: false,
      city: null,
      hasCity: false,
      isLoading: false,
      login: () => {},
      logout: () => {},
      setUserPlans: () => {},
      refreshUserPlans: async () => null,
      sendFormOtp: sendFormOtpRequest,
      resendFormOtp: sendFormOtpRequest,
      verifyFormOtp: verifyFormOtpRequest,
      sendSignupOtp: sendSignupOtpRequest,
      resendSignupOtp: sendSignupOtpRequest,
      verifySignupOtp: verifySignupOtpRequest,
    };
  }
  return ctx;
};

export default AuthContext;
